import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import {
  Input,
  ZoomWrapper,
  Button,
  ConfirmDelete,
  SecondaryButton
} from '../styles/TableStyles';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';
import { mapColumnLabels } from '../styles/ColumnUtils';

const initialFormState = {
  ItemNumber: '',
  Type: '',
  Retailer: '',
  Description: '',
  MethodName: '',
  CasesBinPerLoad: '',
  CasesBinPerPallet: '',
  Ti: '',
  Hi: '',
  Length: '',
  Width: '',
  Height: '',
  CaseNetWeight: '',
  UPC: '',
  Cost: ''
};

// Numeric fields for coercion when submitting
const numberFields = [
  'CasesBinPerLoad', 'CasesBinPerPallet', 'Ti', 'Hi',
  'Length', 'Width', 'Height', 'CaseNetWeight', 'Cost'
];

const visibleColumns = mapColumnLabels(Object.keys(initialFormState)).map(col => {
  return col.key === 'Cost' ? { ...col, format: 'currency' } : col;
});

const MethodsPage = () => {
  const [methods, setMethods] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [submitting, setSubmitting] = useState(false);
  const [editReady, setEditReady] = useState(true);
  const editReadyTimerRef = useRef(null);

  const fetchMethods = useCallback(() => {
    authAxios.get('/api/methods')
      .then(res => setMethods(res.data))
      .catch(err => console.error('Error fetching methods:', err));
  }, []);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      // Only include fields defined in the form model and coerce numbers
      const cleanedForm = {};
      for (const key of Object.keys(initialFormState)) {
        const raw = formData[key];
        if (raw === '' || raw === undefined) {
          cleanedForm[key] = null;
        } else if (numberFields.includes(key)) {
          const n = Number(raw);
          cleanedForm[key] = Number.isFinite(n) ? n : null;
        } else {
          cleanedForm[key] = raw;
        }
      }

      // Guard against accidental immediate submit when entering edit
      if (isEditing && !editReady) {
        return; // ignore accidental submit
      }

      if (isEditing) {
        await authAxios.put(`/api/methods/${editingId}`, cleanedForm);
      } else {
        await authAxios.post('/api/methods', cleanedForm);
      }

      setFormData(initialFormState);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchMethods();
      savedToast('Method');
    } catch (err) {
      console.error('Error submitting form:', err);
      errorToast();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    // Pick only editable fields from the selected row
    const picked = {};
    for (const key of Object.keys(initialFormState)) {
      picked[key] = row[key] ?? '';
    }
    setFormData(picked);
    setShowForm(false); // ensure Add mode is hidden while editing
    setIsEditing(true);
    setEditingId(row.UniqueID);

    // Arm edit mode after a short delay to avoid click-through submits
    if (editReadyTimerRef.current) {
      clearTimeout(editReadyTimerRef.current);
    }
    setEditReady(false);
    editReadyTimerRef.current = setTimeout(() => setEditReady(true), 300);
  };

  const handleDelete = async (id) => {
    try {
      await authAxios.delete(`/api/methods/${id}`);
      fetchMethods();
      deletedToast('Method');
    } catch (err) {
      console.error('Error deleting method:', err);
      errorToast('Error, unable to delete');
    }
  };

  const sortedData = useMemo(() => {
    const sortable = [...methods];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }

        return sortConfig.direction === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }
    return sortable;
  }, [methods, sortConfig]);

  const filteredData = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    return sortedData.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }, [searchQuery, sortedData]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <ZoomWrapper zoom="80%">
      <StandardTableLayout
        title="Methods"
        columns={visibleColumns}
        rows={filteredData}
        editingId={editingId}
        showForm={showForm}
        submitting={submitting}
        onToggleForm={() => {
          setShowForm(!showForm);
          setIsEditing(false);
          setFormData(initialFormState);
        }}
        onSubmit={handleSubmit}
        formData={formData}
        handleChange={handleChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchValue={searchQuery}
        onSearchChange={(e) => setSearchQuery(e.target.value)}
        renderCell={(col, data, handleChange) => {
          if (!handleChange) {
            return (
              <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
                {String(data?.[col] ?? '')}
              </span>
            );
          }
          return (
            <Input
              type={numberFields.includes(col) ? 'number' : 'text'}
              name={col}
              value={data[col] ?? ''}
              onChange={handleChange}
            />
          );
        }}
        renderActions={(row) => (
          editingId === row.UniqueID ? (
            <>
              <Button type="submit" aria-label="Save" disabled={submitting || !editReady}>Save</Button>
              <SecondaryButton
                type="button"
                aria-label="Cancel"
                onClick={() => {
                  setIsEditing(false);
                  setEditingId(null);
                  setFormData(initialFormState);
                  if (editReadyTimerRef.current) {
                    clearTimeout(editReadyTimerRef.current);
                  }
                  setEditReady(true);
                }}
              >
                Cancel
              </SecondaryButton>
            </>
          ) : (
            <>
              <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
              <ConfirmDelete onConfirm={() => handleDelete(row.UniqueID)} />
            </>
          )
        )}
      />
    </ZoomWrapper>
  );
};

export default MethodsPage;
